import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import LocationService from "../../services/locationService";

export const fetchCountries = createAsyncThunk(
  "location/fetchCountries",
  async (_, thunkAPI) => {
    try {
      const res = await LocationService.getAllCountries();

      return res?.data?.data || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Fetch countries failed",
      );
    }
  },
);

export const createCountry = createAsyncThunk(
  "location/createCountry",
  async (payload, thunkAPI) => {
    try {
      const res = await LocationService.createCountry(payload);

      return res?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Create country failed",
      );
    }
  },
);

export const updateCountry = createAsyncThunk(
  "location/updateCountry",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await LocationService.updateCountry(id, data);

      return res?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Update country failed",
      );
    }
  },
);

export const deleteCountry = createAsyncThunk(
  "location/deleteCountry",
  async (id, thunkAPI) => {
    try {
      await LocationService.deleteCountry(id);

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Delete country failed",
      );
    }
  },
);

export const fetchStates = createAsyncThunk(
  "location/fetchStates",
  async (countryId, thunkAPI) => {
    try {
      const res = await LocationService.getAllStates({
        countryId,
      });

      return res?.data?.data || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Fetch states failed",
      );
    }
  },
);

export const createState = createAsyncThunk(
  "location/createState",
  async (payload, thunkAPI) => {
    try {
      const res = await LocationService.createState(payload);

      return res?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Create state failed",
      );
    }
  },
);

export const updateState = createAsyncThunk(
  "location/updateState",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await LocationService.updateState(id, data);

      return res?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Update state failed",
      );
    }
  },
);

export const deleteState = createAsyncThunk(
  "location/deleteState",
  async (id, thunkAPI) => {
    try {
      await LocationService.deleteState(id);

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Delete state failed",
      );
    }
  },
);

export const fetchCities = createAsyncThunk(
  "location/fetchCities",
  async (stateId, thunkAPI) => {
    try {
      const res = await LocationService.getAllCities({
        stateId,
      });

      return res?.data?.data || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Fetch cities failed",
      );
    }
  },
);

export const createCity = createAsyncThunk(
  "location/createCity",
  async (payload, thunkAPI) => {
    try {
      const res = await LocationService.createCity(payload);

      return res?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Create city failed",
      );
    }
  },
);

export const updateCity = createAsyncThunk(
  "location/updateCity",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await LocationService.updateCity(id, data);

      return res?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Update city failed",
      );
    }
  },
);

export const deleteCity = createAsyncThunk(
  "location/deleteCity",
  async (id, thunkAPI) => {
    try {
      await LocationService.deleteCity(id);

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Delete city failed",
      );
    }
  },
);

const locationSlice = createSlice({
  name: "location",

  initialState: {
    countries: [],
    states: [],
    cities: [],

    loading: false,
    error: null,

    successMessage: null,
  },

  reducers: {
    clearStates(state) {
      state.states = [];
      state.cities = [];
    },

    clearCities(state) {
      state.cities = [];
    },

    clearLocationMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
  },

  extraReducers: (builder) => {
    // ================= COUNTRIES =================

    builder
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload;
      })

      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createCountry.fulfilled, (state, action) => {
        state.successMessage = "Country created successfully";
      })

      .addCase(updateCountry.fulfilled, (state, action) => {
        state.successMessage = "Country updated successfully";
      })

      .addCase(deleteCountry.fulfilled, (state, action) => {
        state.countries = state.countries.filter(
          (item) => item.id !== action.payload,
        );

        state.successMessage = "Country deleted successfully";
      });

    builder
      .addCase(fetchStates.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchStates.fulfilled, (state, action) => {
        state.loading = false;
        state.states = action.payload;
      })

      .addCase(fetchStates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createState.fulfilled, (state) => {
        state.successMessage = "State created successfully";
      })

      .addCase(updateState.fulfilled, (state) => {
        state.successMessage = "State updated successfully";
      })

      .addCase(deleteState.fulfilled, (state, action) => {
        state.states = state.states.filter(
          (item) => item.id !== action.payload,
        );

        state.successMessage = "State deleted successfully";
      });

    builder
      .addCase(fetchCities.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchCities.fulfilled, (state, action) => {
        state.loading = false;
        state.cities = action.payload;
      })

      .addCase(fetchCities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createCity.fulfilled, (state) => {
        state.successMessage = "City created successfully";
      })

      .addCase(updateCity.fulfilled, (state) => {
        state.successMessage = "City updated successfully";
      })

      .addCase(deleteCity.fulfilled, (state, action) => {
        state.cities = state.cities.filter(
          (item) => item.id !== action.payload,
        );

        state.successMessage = "City deleted successfully";
      });
  },
});

export const { clearStates, clearCities, clearLocationMessages } =
  locationSlice.actions;

export default locationSlice.reducer;
